import React, { useEffect } from "react";
import { getUserProfiles } from "app/contexts/JWTAuthContext";
import * as storage from "app/data/storage";
import { useRole } from "app/hooks/useRole";

import {
  Typography,
  Grid,
  Box,
  Container,
  Paper,
  Avatar,
  IconButton,
  Tooltip,
  Fade,
} from "@mui/material";
import { MatxLoading } from "app/components";
import PersonIcon from "@mui/icons-material/Person";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { alpha } from "@mui/material/styles";
import { useAuth } from "app/hooks/useAuth";

const WelcomeMessage = ({ userName }: { userName: string }) => (
  <Fade in timeout={1000}>
    <Box sx={{ textAlign: "center", mb: 6 }}>
      <Typography
        variant="h4"
        sx={{
          fontWeight: 600,
          mb: 3,
          background: "linear-gradient(135deg, #002868, #004AAD)",
          backgroundClip: "text",
          textFillColor: "transparent",
          letterSpacing: "-0.5px",
        }}
      >
        Bem-vindo(a), {userName}
      </Typography>
      <Typography
        variant="body1"
        color="text.secondary"
        sx={{
          maxWidth: 500,
          mx: "auto",
          fontSize: 16,
          fontWeight: 500,
          lineHeight: 1.6,
          opacity: 0.85,
        }}
      >
        Selecione seu perfil de acesso para continuar
      </Typography>
    </Box>
  </Fade>
);

const ChangeProfile = ({ isFromLogin }: { isFromLogin?: boolean }) => {
  const selectedProfile = storage.get("role") || "GUEST";
  const { user } = useAuth();
  const userProfileList = getUserProfiles();
  const userProfileArray = Object.values(userProfileList || []);
  const { isDialogOpen } = useRole();

  const sortedUserProfileArray = [...userProfileArray].sort(
    (a, b) => a.id - b.id
  );

  const setRole = (role: string, roleName: string) => {
    storage.set("role", role);
    storage.set("roleName", roleName);
    window.location.reload();
  };

  useEffect(() => {
    if (sortedUserProfileArray.length === 1 && isDialogOpen) {
      setRole(sortedUserProfileArray[0].role, sortedUserProfileArray[0].name);
    }
  }, []); // eslint-disable-line

  if (sortedUserProfileArray.length === 1 && isDialogOpen) {
    return <MatxLoading />;
  }

  return (
    <Container maxWidth="xl">
      <Box sx={{ py: 4 }}>
        {isFromLogin ? (
          <WelcomeMessage userName={user?.name || ""} />
        ) : (
          <Fade in timeout={1000}>
            <Typography
              variant="h4"
              align="center"
              sx={{
                mb: 6,
                fontWeight: 600,
                background:
                  "linear-gradient(45deg, rgba(0,40,104, 0.96), rgba(0,40,104, 0.60))",
                backgroundClip: "text",
                textFillColor: "transparent",
              }}
            >
              {isDialogOpen
                ? "Escolha seu perfil de acesso"
                : "Selecione seu perfil"}
            </Typography>
          </Fade>
        )}
        <Fade in timeout={isFromLogin ? 2000 : 1000}>
          <Grid container spacing={3} justifyContent="center">
            {sortedUserProfileArray.map((item) => (
              <Grid item xs={12} sm={6} md={4} key={item.id}>
                <Paper
                  onClick={() =>
                    item.role !== selectedProfile &&
                    setRole(item.role, item.name)
                  }
                  sx={{
                    position: "relative",
                    p: 4,
                    height: "180px",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor:
                      item.role === selectedProfile ? "default" : "pointer",
                    transition: "all 0.2s ease",
                    background:
                      item.role === selectedProfile
                        ? "linear-gradient(to right, #f5f7fa, #c3cfe2)"
                        : "#ffffff",
                    borderRadius: "16px",
                    border:
                      item.role === selectedProfile
                        ? "2px solid #c3cfe2"
                        : "1px solid #edf2f7",
                    "&:hover": {
                      transform:
                        item.role === selectedProfile
                          ? "none"
                          : "translateY(-4px)",
                      boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
                      background:
                        item.role === selectedProfile
                          ? "linear-gradient(to right, #f5f7fa, #c3cfe2)"
                          : "#f8fafc",
                    },
                  }}
                >
                  <Avatar
                    sx={{
                      width: 80,
                      height: 80,
                      mb: 2,
                      background:
                        item.role === selectedProfile
                          ? "#94a3b8"
                          : "linear-gradient(135deg, rgba(0,40,104, 0.96), #818cf8)",
                      boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
                    }}
                  >
                    {item.role === selectedProfile ? (
                      <CheckCircleIcon sx={{ fontSize: 28 }} />
                    ) : (
                      <img
                        src={require(`../../assets/icons/${item.icon}.png`)}
                        alt="logo"
                        width={55}
                        style={{ filter: "brightness(0) invert(1)" }}
                      />
                    )}
                  </Avatar>

                  <Typography
                    variant="h6"
                    align="center"
                    sx={{
                      fontWeight: 500,
                      fontSize: "1.1rem",
                      color:
                        item.role === selectedProfile ? "#475569" : "#64748b",
                      letterSpacing: "0.2px",
                    }}
                  >
                    {item.name}
                  </Typography>

                  {item.role === selectedProfile && (
                    <Typography
                      variant="caption"
                      sx={{
                        mt: 1,
                        color: "#64748b",
                        fontWeight: 400,
                        fontSize: "0.75rem",
                        letterSpacing: "0.4px",
                      }}
                    >
                      Perfil Atual
                    </Typography>
                  )}
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Fade>
      </Box>
    </Container>
  );
};

export default ChangeProfile;
